import React from "react";
import { graphql, useFragment } from "react-relay";

import {
	faArrowFromLeft,
	faCalendarMinus,
	faEllipsisV,
	faPen,
	faTimes,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import EventEmitter from "events";
import { commitMutation } from "relay-runtime";

import { environment } from "~/models";
import { HomePanelContext } from "~/nav/panels/Home";
import HomePanelGroupsMenu from "~/stream/HomePanelGroupsMenu";
import IconButton from "~/ui/legacy/IconButton";
import Menu from "~/ui/menu";

import type { GroupsMenu_stream$key as GroupMenuStreamType } from "~/__generated__/GroupsMenu_stream.graphql";
import type { GroupsMenuRemoveObjectMutation as GroupsMenuRemoveObjectMutationType } from "~/__generated__/GroupsMenuRemoveObjectMutation.graphql";

import styles from "~/nav/panels/shared/PanelSection.module.css";

export const emitter = new EventEmitter();
export const HOME_PANEL_ITEM_REMOVED_EVENT_KEY = "homePanelItemRemoved";

const groupsMenuStreamFragment = graphql`
	fragment GroupsMenu_stream on NoteStream {
		...HomePanelGroupsMenu_object
		id
		homePanelGroup {
			id
		}
	}
`;

const GroupsMenuRemoveObjectMutation = graphql`
	mutation GroupsMenuRemoveObjectMutation($id: ID!) {
		removeObjectFromHomePanel(objectId: $id) {
			removedObject {
				...HomePanelGroupMenuButton_stream
				...HomePanelGroupsMenu_object
				...GroupsMenu_stream
			}
			removedFromGroup {
				...HomePanelGroup_group
			}
			viewer {
				homePanelGroups {
					...HomePanelGroup_group
				}
			}
		}
	}
`;

type Props = {
	isMenuOpen: boolean;
	eventId?: string;
	stream?: GroupMenuStreamType;
	hideMeeting?: () => void;
	editEvent?: () => void;
	toggleOpen: (open: boolean) => void;
	fromMeetingsSection?: boolean;
};

export default function GroupsMenu({ eventId, isMenuOpen, stream, hideMeeting, editEvent, toggleOpen }: Props) {
	const streamData = useFragment(groupsMenuStreamFragment, stream || null);

	const panelContext = React.useContext(HomePanelContext);

	const handleRemove = React.useCallback(() => {
		if (!streamData) return;

		commitMutation<GroupsMenuRemoveObjectMutationType>(environment, {
			mutation: GroupsMenuRemoveObjectMutation,
			variables: {
				id: streamData.id,
			},
			onCompleted: () => emitter.emit(HOME_PANEL_ITEM_REMOVED_EVENT_KEY),
		});
	}, [streamData]);

	if (!panelContext.viewer) return <></>;

	const hasCurrentGroup = Boolean(streamData?.homePanelGroup?.id);

	return (
		<Menu open={isMenuOpen} onOpenChange={toggleOpen}>
			<Menu.Trigger>
				<IconButton
					title="Options"
					icon={faEllipsisV}
					className={cx(styles.menuButton, { [styles.menuButtonVisible]: isMenuOpen })}
				/>
			</Menu.Trigger>
			<Menu.Content align="end" className={styles.menuContent}>
				<Menu.Sub>
					<Menu.SubTrigger icon={faArrowFromLeft}>{hasCurrentGroup ? "Move to group" : "Add to group"}</Menu.SubTrigger>
					<Menu.SubContent className={styles.menuContent}>
						<HomePanelGroupsMenu object={streamData} eventId={eventId} viewer={panelContext.viewer} />
					</Menu.SubContent>
				</Menu.Sub>
				{hideMeeting ? (
					<>
						{editEvent && (
							<Menu.Item icon={faPen} onSelect={editEvent}>
								Edit event
							</Menu.Item>
						)}
						<Menu.Item icon={faCalendarMinus} onSelect={hideMeeting}>
							Hide event from Fellow
						</Menu.Item>
					</>
				) : (
					<Menu.Item icon={faTimes} onSelect={handleRemove}>
						Remove from home panel
					</Menu.Item>
				)}
			</Menu.Content>
		</Menu>
	);
}
